import type { ModalProps } from 'react-native';

import { EVENT_FONT } from '../Event/eventTheme';

export const DUEL_HUD_FONT = EVENT_FONT;

export const duelHudColors = {
	textPrimary: '#F2F6FF',
	textSecondary: '#B2C4DE',
	success: '#0CEB66',
	players: '#39EDB5',
	time: '#FFE9A8',
	counter: '#67E8F8',
	accent: '#4ED8FF',
	stroke: '#42C8FF',
	divider: 'rgba(66,200,255,0.35)',
	dividerClear: 'rgba(66,200,255,0)',
	historyBorder: '#2E6699',
	counterBorder: '#214D74',
	pillFill: '#0B3326',
	pillBorder: '#1E8F5C',
	leaveBorder: '#EC514A',
	leaveGlow: 'rgba(255,59,48,0.3)',
} as const;

export const DUEL_HUD_SHELL_FILL: Array<[string, string]> = [
	['0', '#0C2037'],
	['0.78', '#061322'],
	['1', '#061322'],
];

export const DUEL_HUD_HISTORY_FILL: Array<[string, string]> = [
	['0', '#12305A'],
	['1', '#071631'],
];

export const DUEL_HUD_COUNTER_FILL: Array<[string, string]> = [
	['0', '#0E2540'],
	['1', '#071727'],
];

export const DUEL_HUD_LEAVE_FILL: Array<[string, string]> = [
	['0', '#B3322F'],
	['1', '#5A0F16'],
];

export const DUEL_HUD_DESKTOP_SCALE = 1.5;
export const DUEL_HUD_DESKTOP_MIN_HEIGHT = 640;

export const DUEL_HUD_PORTRAIT = {
	width: 378,
	barHeight: 44,
	tab: 16,
	side: 6,
	topGap: 2,
	radius: 16,
	inset: 4,
	columnGap: 7,
	iconGap: 4,
	buttonHeight: 36,
	drawerPadding: 16,
	drawerGap: 12,
	compactBelow: 370,
} as const;

export const DUEL_HUD_RAIL = {
	width: 82,
	height: 284,
	columnWidth: 66,
	sheetWidth: 474,
	desktopSheetWidth: 735,
	desktopHeight: 428,
	tab: 16,
	tabTop: 134,
	edgeTop: 122,
	phoneLeft: 47,
	phoneTopRatio: 69 / 390,
	desktopLeft: 16,
	desktopTopRatio: 201 / 764,
	bodyPadding: 16,
	bodyGap: 12,
} as const;

export const DUEL_HUD_SHELL_MARGIN = { left: 20, right: 20, top: 16, bottom: 28 } as const;

const pad2 = (value: number) => String(value).padStart(2, '0');

export const formatDuelHudCountdown = (ms: number) => {
	const totalSeconds = Math.max(0, Math.floor(ms / 1000));
	const hours = Math.floor(totalSeconds / 3600);
	const minutes = Math.floor((totalSeconds % 3600) / 60);
	const seconds = totalSeconds % 60;
	return hours > 0 ? `${hours}:${pad2(minutes)}:${pad2(seconds)}` : `${minutes}:${pad2(seconds)}`;
};

export type DuelHudCounts = {
	players: number;
	maxPlayers: number;
	finished: number;
	stillPlaying: number;
};

export const duelHudCounts = (duel: {
	participantCount?: number;
	currentPlayers: number;
	maxPlayers: number;
	finishedCount?: number;
}): DuelHudCounts => {
	const players = Math.max(0, duel.participantCount ?? duel.currentPlayers ?? 0);
	const finished = Math.min(players, Math.max(0, duel.finishedCount ?? 0));
	return {
		players,
		maxPlayers: Math.max(0, duel.maxPlayers ?? 0),
		finished,
		stillPlaying: players - finished,
	};
};

export const duelHudDeadline = (duel: {
	currentPlayerDeadlineAt?: string | null;
	expiresAt: string | null;
}): number | null => {
	const iso = duel.currentPlayerDeadlineAt ?? duel.expiresAt;
	if (!iso) return null;
	const time = new Date(iso).getTime();
	return Number.isFinite(time) ? time : null;
};

export const DUEL_RESULT_LAYOUT = {
	portraitWidth: 366,
	portraitSide: 12,
	landscapeWidth: 750,
	landscapeHeight: 360,
	desktopWidth: 1152,
	desktopHeight: 540,
	landscapeMargin: 15,
	radius: 24,
} as const;

export const DUEL_RESULT_BACKDROP = 'rgba(1,6,12,0.9)';

export const snapDesktopFont = (size: number, scale: number) =>
	scale === DUEL_HUD_DESKTOP_SCALE ? Math.ceil((size * scale) / 2) * 2 : size * scale;

export const DUEL_MODAL_ORIENTATIONS: ModalProps['supportedOrientations'] = [
	'portrait',
	'landscape',
	'landscape-left',
	'landscape-right',
];

export const DUEL_HUD_MENU = {
	plate: 36,
	gap: 4,
	desktopGap: 6,
	portraitSlot: 44,
	phoneTop: 9,
	phoneRightExtra: 41,
	desktopTop: 15,
	desktopRight: 64,
} as const;
